// meetingRooms/hooks/useGallery.js
import { useState, useRef } from 'react';
import {
    uploadResourceImage,
    deleteResourceImage,
    reorderResourceImages
} from 'api';

export const useGallery = (allMeetingRooms, showSnackbar) => {
    const [galleryFormData, setGalleryFormData] = useState({});
    const [newImages, setNewImages] = useState({});
    const [isSavingGallery, setIsSavingGallery] = useState(false);
    const isSavingRef = useRef(false);

    // Gallery image upload - adds to pending uploads
    const handleImageUpload = (meetingRoomSlug, files) => {
        const newImageFiles = Array.from(files).map((file, index) => ({
            id: `new-${Date.now()}-${index}`,
            file: file,
            name: file.name,
            isTemp: true,
            url: URL.createObjectURL(file)
        }));

        setNewImages(prev => ({
            ...prev,
            [meetingRoomSlug]: [...(prev[meetingRoomSlug] || []), ...newImageFiles]
        }));

        showSnackbar(`${files.length} image(s) added for upload`, 'info');
    };

    // Save Gallery - only uploads new images
    const handleSaveGallery = async (meetingRoomSlug) => {
        const newImagesData = newImages[meetingRoomSlug];

        if (!newImagesData || newImagesData.length === 0) {
            showSnackbar("No new images to upload", "info");
            return;
        }

        try {
            setIsSavingGallery(true);
            isSavingRef.current = true;

            const meetingRoom = allMeetingRooms.find(mr => mr.slug === meetingRoomSlug);
            if (!meetingRoom) {
                throw new Error("Meeting room not found");
            }

            // Upload each new image and collect the responses
            const tempImages = newImagesData.filter(img => img.isTemp);
            const uploadedImages = [];
            let successCount = 0;

            for (let i = 0; i < tempImages.length; i++) {
                const image = tempImages[i];

                try {
                    const formData = new FormData();
                    formData.append('image', image.file);
                    formData.append('resource_slug', meetingRoomSlug);
                    formData.append('name', image.name || `Image ${Date.now()}`);
                    formData.append('status', 'AC');

                    const result = await uploadResourceImage(formData);

                    // Handle different response structures
                    let imageData = null;

                    if (result?.data?.data) {
                        // Response structure: { success: true, data: { image data } }
                        imageData = result.data.data;
                    } else if (result?.data) {
                        // Response structure: { image data }
                        imageData = result.data;
                    } else if (result) {
                        // Direct image data
                        imageData = result;
                    }

                    if (imageData) {
                        uploadedImages.push(imageData);
                        successCount++;
                    }

                } catch (uploadError) {
                    console.error(`❌ Error uploading image ${i + 1}:`, uploadError);
                    showSnackbar(`Failed to upload ${image.name}: ${uploadError.message}`, "error");
                }
            }

            if (successCount === 0) {
                throw new Error("No images were uploaded successfully");
            }

            // Update galleryFormData with the newly uploaded images
            setGalleryFormData(prev => {
                const currentImages = prev[meetingRoomSlug] || [];

                // Sort all images by order_line to maintain proper order
                const allImages = [...currentImages, ...uploadedImages].sort((a, b) =>
                    (a.order_line || 0) - (b.order_line || 0)
                );

                return {
                    ...prev,
                    [meetingRoomSlug]: allImages
                };
            });

            // Clear new images after upload
            setNewImages(prev => ({
                ...prev,
                [meetingRoomSlug]: []
            }));

            // Revoke object URLs to prevent memory leaks
            tempImages.forEach(img => {
                if (img.url && img.url.startsWith('blob:')) {
                    URL.revokeObjectURL(img.url);
                }
            });

            if (successCount === tempImages.length) {
                showSnackbar(`${successCount} image(s) uploaded successfully`, "success");
            } else {
                showSnackbar(`${successCount} of ${tempImages.length} images uploaded successfully`, "warning");
            }

        } catch (error) {
            console.error("❌ Error uploading images:", error);
            showSnackbar("Failed to upload images: " + error.message, "error");
        } finally {
            setIsSavingGallery(false);
            isSavingRef.current = false;
        }
    };

    // Cancel Gallery - clears pending uploads
    const handleCancelGallery = (meetingRoomSlug) => {
        const currentNewImages = newImages[meetingRoomSlug] || [];

        // Revoke object URLs to prevent memory leaks
        currentNewImages.forEach(img => {
            if (img.url && img.url.startsWith('blob:')) {
                URL.revokeObjectURL(img.url);
            }
        });

        setNewImages(prev => ({
            ...prev,
            [meetingRoomSlug]: []
        }));

        showSnackbar('Pending uploads cancelled', 'info');
    };

    const handleImageDelete = async (meetingRoomSlug, index, isNew = false) => {
        try {
            if (isNew) {
                // Remove from new images (no API call needed - they're not on server yet)
                const currentNewImages = newImages[meetingRoomSlug] || [];
                const newImagesList = [...currentNewImages];
                const deletedImage = newImagesList.splice(index, 1)[0];

                setNewImages(prev => ({
                    ...prev,
                    [meetingRoomSlug]: newImagesList
                }));

                // Revoke object URL to prevent memory leaks
                if (deletedImage.url && deletedImage.url.startsWith('blob:')) {
                    URL.revokeObjectURL(deletedImage.url);
                }

                showSnackbar('Image removed from upload queue', 'success');
            } else {
                // Remove from existing images - immediate API call
                const currentImages = galleryFormData[meetingRoomSlug] || [];
                const newImagesList = [...currentImages];
                const deletedImage = newImagesList.splice(index, 1)[0];

                // Call delete API first
                if (deletedImage.slug) {
                    try {
                        const result = await deleteResourceImage(deletedImage.slug);
                        if (result && result.status && result.status >= 400) {
                            throw new Error(`Delete failed with status ${result.status}`);
                        }
                    } catch (error) {
                        console.error('Error calling delete API:', error);
                        showSnackbar('Failed to delete image from server: ' + error.message, 'error');
                        return; // Don't update UI if API call failed
                    }
                }

                // Update UI after successful API call
                setGalleryFormData(prev => ({
                    ...prev,
                    [meetingRoomSlug]: newImagesList
                }));

                showSnackbar('Image deleted successfully', 'success');
            }
        } catch (error) {
            console.error('Error deleting image:', error);
            showSnackbar('Failed to delete image: ' + error.message, 'error');
        }
    };

    // Image move left - immediate reordering for existing images
    const handleImageMoveLeft = async (meetingRoomSlug, index, isNew = false) => {
        if (index === 0) return;

        try {
            if (isNew) {
                // Just reorder in memory for new images
                const currentImages = newImages[meetingRoomSlug] || [];
                const newImagesList = [...currentImages];
                const temp = newImagesList[index];
                newImagesList[index] = newImagesList[index - 1];
                newImagesList[index - 1] = temp;

                setNewImages(prev => ({
                    ...prev,
                    [meetingRoomSlug]: newImagesList
                }));
            } else {
                // Immediate reorder for existing images
                const currentImages = galleryFormData[meetingRoomSlug] || [];
                const newImagesList = [...currentImages];
                const temp = newImagesList[index];
                newImagesList[index] = newImagesList[index - 1];
                newImagesList[index - 1] = temp;

                // Update UI immediately
                setGalleryFormData(prev => ({
                    ...prev,
                    [meetingRoomSlug]: newImagesList
                }));

                // Call reorder API immediately
                const reorderData = {
                    resource_slug: meetingRoomSlug,
                    images: newImagesList.map((img, idx) => ({
                        slug: img.slug,
                        order_line: idx + 1
                    }))
                };

                try {
                    await reorderResourceImages(reorderData);
                    showSnackbar('Images reordered successfully', 'success');
                } catch (error) {
                    console.warn('Error reordering images:', error);
                    showSnackbar('Image moved but order may not be saved', 'warning');
                }
            }
        } catch (error) {
            console.error('Error moving image:', error);
            showSnackbar('Failed to move image', 'error');
        }
    };

    // Image move right - immediate reordering for existing images
    const handleImageMoveRight = async (meetingRoomSlug, index, isNew = false) => {
        const relevantArray = isNew ? (newImages[meetingRoomSlug] || []) : (galleryFormData[meetingRoomSlug] || []);
        if (index >= relevantArray.length - 1) return;

        try {
            if (isNew) {
                // Just reorder in memory for new images
                const currentImages = newImages[meetingRoomSlug] || [];
                const newImagesList = [...currentImages];
                const temp = newImagesList[index];
                newImagesList[index] = newImagesList[index + 1];
                newImagesList[index + 1] = temp;

                setNewImages(prev => ({
                    ...prev,
                    [meetingRoomSlug]: newImagesList
                }));
            } else {
                // Immediate reorder for existing images
                const currentImages = galleryFormData[meetingRoomSlug] || [];
                const newImagesList = [...currentImages];
                const temp = newImagesList[index];
                newImagesList[index] = newImagesList[index + 1];
                newImagesList[index + 1] = temp;

                // Update UI immediately
                setGalleryFormData(prev => ({
                    ...prev,
                    [meetingRoomSlug]: newImagesList
                }));

                // Call reorder API immediately
                const reorderData = {
                    resource_slug: meetingRoomSlug,
                    images: newImagesList.map((img, idx) => ({
                        slug: img.slug,
                        order_line: idx + 1
                    }))
                };

                try {
                    await reorderResourceImages(reorderData);
                    showSnackbar('Images reordered successfully', 'success');
                } catch (error) {
                    console.warn('Error reordering images:', error);
                    showSnackbar('Image moved but order may not be saved', 'warning');
                }
            }
        } catch (error) {
            console.error('Error moving image:', error);
            showSnackbar('Failed to move image', 'error');
        }
    };

    const initializeGallery = (meetingRooms) => {
        const newGalleryFormData = {};
        const newNewImages = {};

        meetingRooms.forEach(meetingRoom => {
            const galleryData = meetingRoom.images ? [...meetingRoom.images].sort((a, b) =>
                (a.order_line || 0) - (b.order_line || 0)
            ) : [];

            newGalleryFormData[meetingRoom.slug] = galleryData;
            newNewImages[meetingRoom.slug] = newImages[meetingRoom.slug] || [];
        });

        setGalleryFormData(newGalleryFormData);
        setNewImages(newNewImages);
    };

    return {
        galleryFormData,
        newImages,
        isSavingGallery,
        isSavingRef,
        handleImageUpload,
        handleSaveGallery,
        handleCancelGallery,
        handleImageDelete,
        handleImageMoveLeft,
        handleImageMoveRight,
        initializeGallery
    };
};